import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { User, Badge, StudentProgressRecord, StudentReport, DetailedStudentAnalytics } from '../../types';
import LoadingSpinner from '../ui/LoadingSpinner';
import BadgeIcon from '../ui/BadgeIcon';
import KeyboardHeatmap from '../ui/KeyboardHeatmap';
import GameStatsGrid from '../ui/GameStatsGrid';
import ActivityChart from '../ui/ActivityChart';
import { api } from '../../utils/api';
import PlaybackModal from '../modals/PlaybackModal';

interface StudentProgressTabProps {
  student: User;
}

const StatCard: React.FC<{ label: string; value: string; icon: string; color: string }> = ({ label, value, icon, color }) => (
  <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200 text-center">
    <div className="text-3xl mb-1">{icon}</div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    <div className="text-xs uppercase text-gray-500 tracking-wide">{label}</div>
  </div>
);

const StudentProgressTab: React.FC<StudentProgressTabProps> = ({ student }) => {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [progressData, setProgressData] = useState<any[]>([]);
  const [progressRecords, setProgressRecords] = useState<StudentProgressRecord[]>([]);
  const [report, setReport] = useState<StudentReport | null>(null);
  const [analytics, setAnalytics] = useState<DetailedStudentAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playingBackSessionId, setPlayingBackSessionId] = useState<string | null>(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const [badgeData, progressRes, reportData, analyticsData] = await Promise.all([
          api.get<Badge[]>(`/api/v1/student/${student.user_id}/badges`),
          api.get<StudentProgressRecord[]>(`/api/v1/student/${student.user_id}/progress`),
          api.get<StudentReport>(`/api/v1/student/${student.user_id}/report`),
          api.get<DetailedStudentAnalytics>(`/api/v1/student/${student.user_id}/analytics`),
        ]);

        setBadges(badgeData);
        setProgressRecords(progressRes);
        setProgressData(progressRes.map(p => ({
          name: p.title.length > 15 ? p.title.substring(0, 12) + '...' : p.title,
          WPM: p.wpm,
          Accuracy: p.accuracy,
        })).reverse());
        setReport(reportData);
        setAnalytics(analyticsData);
      } catch (e) {
        if (e instanceof Error) setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [student]);

  const renderChart = () => {
    if (progressData.length > 0) {
      return (
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <LineChart data={progressData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" label={{ value: 'WPM', angle: -90, position: 'insideLeft' }} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ value: 'Accuracy (%)', angle: 90, position: 'insideRight' }} />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="WPM" stroke="#3b82f6" strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="Accuracy" stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    }
    return <p className="text-center text-gray-600">No homework or custom lessons completed yet.</p>;
  };

  if (loading) return <LoadingSpinner message="Loading progress..." />;
  if (error) return <div className="text-red-500 bg-red-100 p-4 rounded-lg text-center">{error}</div>;

  const tutorialPct = report && report.tutorial_total > 0 ? Math.round((report.tutorial_completed / report.tutorial_total) * 100) : 0;

  return (
    <div className="space-y-8">
      {playingBackSessionId && <PlaybackModal progressId={playingBackSessionId} onClose={() => setPlayingBackSessionId(null)} />}

      {/* --- Summary at a glance --- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Curriculum" value={`${tutorialPct}%`} icon="📘" color="text-blue-600" />
        <StatCard label="Current Streak" value={`${report?.current_streak ?? 0} day${(report?.current_streak ?? 0) === 1 ? '' : 's'}`} icon="🔥" color="text-orange-600" />
        <StatCard label="Badges Earned" value={String(badges.length)} icon="🏅" color="text-purple-600" />
        <StatCard label="Best Streak" value={`${report?.longest_streak ?? 0} day${(report?.longest_streak ?? 0) === 1 ? '' : 's'}`} icon="⭐" color="text-yellow-600" />
      </div>

      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-bold text-gray-800">Curriculum Progress</h2>
          <span className="text-sm text-gray-500">{report?.tutorial_completed ?? 0} / {report?.tutorial_total ?? 0} lessons</span>
        </div>
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-500" style={{ width: `${tutorialPct}%` }} />
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-xl font-bold mb-4 text-gray-800">Activity (Last 14 Days)</h2>
        {report && <ActivityChart activity={report.activity} />}
      </div>

      {analytics && analytics.problem_keys && Object.keys(analytics.problem_keys).length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-bold mb-4 text-gray-800">Your Key Hotspots</h2>
          <p className="text-sm text-gray-500 mb-3">These are the keys you mix up most often -- a little extra practice here goes a long way.</p>
          <KeyboardHeatmap data={analytics.problem_keys} title="" />
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-xl font-bold mb-4 text-gray-800">Games</h2>
        {report && <GameStatsGrid games={report.games} rpgLevel={report.rpg_level} />}
      </div>

      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Homework &amp; Custom Lessons Over Time</h2>
        {renderChart()}
      </div>

      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Detailed Report</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lesson</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WPM</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Accuracy</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feedback</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {progressRecords.length > 0 ? progressRecords.map(record => (
                <tr key={record.progress_id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.title}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.wpm}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.accuracy}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-semibold">{record.teacher_grade || '-'}</td>
                  <td className="px-6 py-4 whitespace-normal text-sm text-gray-500">{record.teacher_feedback || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {record.has_playback && (
                        <button onClick={() => setPlayingBackSessionId(record.progress_id)} className="text-blue-600 hover:underline">Playback</button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">No lesson data available.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Awards</h2>
        {badges.length === 0 ? (
          <p className="text-gray-600 text-center">No badges earned yet. Keep practicing!</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {badges.map(badge => (
              <div key={badge.badge_name} className="flex flex-col items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-20 h-20 mb-2"><BadgeIcon iconName={badge.icon_name} /></div>
                <h3 className="font-semibold text-gray-800">{badge.badge_name}</h3>
                <p className="text-sm text-gray-500 text-center">{badge.description}</p>
                <p className="text-xs text-gray-400 mt-1">Earned: {new Date(badge.earned_at!).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentProgressTab;
